// A league's rules on the wire: read, the versions, a change, and its preview.
// LEAGUE_SETTINGS.md, LEAGUE_SETUP.md (the rules page), scenario B3,
// PLAN-2026-09-26-every-screen-and-crayon P1 and P2.
//
// The sentences are served, not only the settings: a client with no rules
// engine renders the rules page from `document` and each version from its
// `changes`, the same argument `LeaguePreview` makes for the wizard (audit 3.6).

import { z } from 'zod';
import { RulesDocument } from '../rules/describe';
import { FixedRule, LeagueStage, RulesChange } from '../rules/edit';
import { LeagueSettings, LockableSection } from '../rules/schemas';
import { ScoringTable } from '../rules/scoring-table';
import { LeagueRef, TeamRef } from './views';

/**
 * A rule change put to the league's vote, while it is open (LEAGUE_SETTINGS.md
 * "Votes", scenario B6).
 *
 * COUNTS, NEVER A VOTER LIST, as the commissioner election: how many said yes
 * and no out of how many may vote, and the reader's own vote.
 */
export const RulesProposal = z.object({
  id: z.string(),
  /** Who put it to the vote: the commissioner's name. */
  by: z.string(),
  reason: z.string(),
  createdAt: z.string(),
  /** When it fails if not enough of the league has said yes. */
  closesAt: z.string(),
  /** The version it changes. */
  basedOn: z.number().int().min(1),
  /** The rules it would make — what the preview scores. */
  settings: LeagueSettings,
  /** What it changes, said as the versions list says a version. */
  changes: z.array(RulesChange),
  votes: z.object({
    yes: z.number().int().min(0),
    no: z.number().int().min(0),
    /** Everybody who may vote: the managers with a team when it opened. */
    of: z.number().int().min(0),
    /** The yeses that pass it: more than half of `of`, or the league's supermajority of it. */
    needed: z.number().int().min(1),
  }),
  myVote: z.enum(['yes', 'no']).nullable(),
  /** The reader is one of the managers who may vote, and it is still open. */
  canVote: z.boolean(),
  /** The commissioner or a co-commissioner, while it is open. */
  canWithdraw: z.boolean(),
  /** False once its time is up, while the clock has yet to count it. */
  open: z.boolean(),
});
export type RulesProposal = z.infer<typeof RulesProposal>;

/** `POST /api/leagues/:id/settings/proposal/vote`. */
export const RulesVoteRequest = z.object({
  vote: z.enum(['yes', 'no']),
});
export type RulesVoteRequest = z.infer<typeof RulesVoteRequest>;

/** `GET /api/leagues/:id/settings` — the rules a league plays under now. */
/** A team as the rules read it: its name and its division (`LeagueRulesView.teams`). */
export const RulesTeam = z.object({
  id: z.string(),
  name: z.string(),
  divisionId: z.string().nullable(),
  /**
   * The team it plays in a rivalry week (`teams.rival_team_id`), or null.
   * Added 2026-10-29 (PLAN-2026-10-29-the-schedule-before-the-season); absent
   * reads as none.
   */
  rivalId: z.string().nullable().default(null),
});
export type RulesTeam = z.infer<typeof RulesTeam>;

export const LeagueRulesView = z.object({
  league: LeagueRef,
  /** The version in force; a save names it back as `basedOn`. */
  version: z.number().int().min(1),
  settings: LeagueSettings,
  document: RulesDocument,
  /**
   * The scoring as a table to read: a section per position the league
   * starts, one value per row, the usual numbers beside any that differ
   * (PLAN-2026-10-18 §2.2). Built on the server so a client reads it without
   * the sport package. Added 2026-10-18.
   */
  scoringTable: ScoringTable,
  /** True for the commissioner; everyone else reads. */
  canEdit: z.boolean(),
  /** Past the draft: a change needs a reason, and the locked sections need unlocking. */
  drafted: z.boolean(),
  /** `governance.lockedAfterDraft` once the league has drafted, and empty before. */
  locked: z.array(LockableSection),
  /**
   * Where the league is in its year, which decides what can still change
   * (DIRECTION A17): anything but the seats before the draft; the schedule
   * and the playoffs until the season opens. Added 2026-10-18; absent reads
   * as the season open, the only stage there was.
   */
  stage: LeagueStage.default('in-season'),
  /** What cannot change at this stage, with the reason to show beside it. */
  fixed: z.array(FixedRule),
  /**
   * The first week a scoring change saved now would count in — the first one
   * not yet started. Null when no week is left to play.
   */
  appliesFrom: z.object({ period: z.number().int(), label: z.string() }).nullable(),
  /**
   * A change goes to the league's vote rather than being saved: the rules say
   * so (`governance.changesRequire`) and the league has drafted (B6). The
   * editor's Save is then "Put it to a vote", `POST …/settings/proposal`.
   */
  changesGoToVote: z.boolean().default(false),
  /** The rule change up for a vote, or null when none is. Added 2026-10-01 (B6). */
  proposal: RulesProposal.nullable().default(null),
  /**
   * Every team in seat order with its division, so the rules can say who is
   * in each and a commissioner can move a team (`PUT
   * …/teams/:teamId/division`). A division is one `settings.schedule.divisions`
   * names, or null. Added 2026-10-29 (PLAN-2026-10-29-divisions §4); absent
   * reads as none.
   */
  teams: z.array(RulesTeam).default([]),
});
export type LeagueRulesView = z.infer<typeof LeagueRulesView>;

/**
 * `PUT /api/leagues/:id/teams/:teamId/division`: put a team in one of the
 * league's divisions, or take it out with null. A commissioner's, until the
 * season opens (PLAN-2026-10-29-divisions §3). Answers `LeagueRulesView`.
 */
export const SetDivisionRequest = z.object({
  divisionId: z.string().min(1).nullable(),
});
export type SetDivisionRequest = z.infer<typeof SetDivisionRequest>;

/**
 * `PUT /api/leagues/:id/teams/:teamId/rival`: name the team this one plays in
 * a rivalry week, or null for none. Rivals are a pair, so naming one ends
 * both teams' old pairings. A commissioner's, until the season opens
 * (PLAN-2026-10-29-the-schedule-before-the-season §2). Answers
 * `LeagueRulesView`.
 */
export const SetRivalRequest = z.object({
  rivalTeamId: z.string().min(1).nullable(),
});
export type SetRivalRequest = z.infer<typeof SetRivalRequest>;

export const RulesVersion = z.object({
  version: z.number().int().min(1),
  createdAt: z.string(),
  /** Who saved it: a manager's name, or "The league" for what the site wrote. */
  author: z.string(),
  reason: z.string(),
  effectiveFromPeriod: z.number().int(),
  /**
   * When this version's scoring change counts from — "Week 5", or "next
   * season" for a save after the last week — and null when it changed no
   * scoring, or was saved before the season (it counts from the start). The
   * rest of a version applies the moment it is saved (B-196).
   */
  scoringFrom: z.string().nullable(),
  /** The locked sections this save unlocked, said on the version so nobody wonders. */
  unlocked: z.array(z.string()),
  /** Empty for the first version, which changed nothing. */
  changes: z.array(RulesChange),
});
export type RulesVersion = z.infer<typeof RulesVersion>;

/** `GET /api/leagues/:id/settings/versions` — every version, newest first. */
export const RulesVersionsView = z.object({
  versions: z.array(RulesVersion),
});
export type RulesVersionsView = z.infer<typeof RulesVersionsView>;

/** `POST /api/leagues/:id/settings`. */
export const SaveRulesRequest = z.object({
  settings: LeagueSettings,
  /** Required once the league has drafted; everyone sees it. */
  reason: z.string().max(500).default(''),
  /** The locked sections this save changes, unlocked on purpose. */
  unlock: z.boolean().default(false),
  /**
   * The version the editor started from. A save against an older one is
   * `409`: somebody else changed the rules meanwhile, and saving over them
   * would undo their change without anyone seeing it.
   */
  basedOn: z.number().int().min(1),
});
export type SaveRulesRequest = z.infer<typeof SaveRulesRequest>;

// A refused save is `400 invalid` with every issue in `details.issues`, so
// the editor can mark each field; a save that goes through answers the new
// `LeagueRulesView`.

/** `POST /api/leagues/:id/settings/preview`. */
export const PreviewRulesRequest = z.object({
  settings: LeagueSettings,
});
export type PreviewRulesRequest = z.infer<typeof PreviewRulesRequest>;

export const RulesPreviewRow = z.object({
  team: TeamRef,
  /** The week's total under the rules in force. */
  current: z.number(),
  /** The same starters and the same stat lines under the candidate. */
  candidate: z.number(),
  delta: z.number(),
  rankNow: z.number().int(),
  rankThen: z.number().int(),
});
export type RulesPreviewRow = z.infer<typeof RulesPreviewRow>;

/**
 * The last week with games in it, scored both ways. `period` is null before
 * any game has been played, and `rows` is then empty: there is nothing to
 * preview a change against.
 */
export const RulesPreviewView = z.object({
  period: z.number().int().nullable(),
  label: z.string().nullable(),
  /** `live` while that week's games are still going. */
  status: z.enum(['none', 'live', 'final']),
  rows: z.array(RulesPreviewRow),
});
export type RulesPreviewView = z.infer<typeof RulesPreviewView>;
